/**
 * Calculates the angle in degrees from a point to a center point.
 * Returns angle in range 0-359 where:
 * - 0 degrees is directly below the center (6 o'clock position)
 * - 90 degrees is to the right (3 o'clock position)
 * - 180 degrees is above (12 o'clock position)
 * - 270 degrees is to the left (9 o'clock position)
 *
 * @param {number} mouseX - The x coordinate of the mouse position
 * @param {number} mouseY - The y coordinate of the mouse position
 * @param {number} centerX - The x coordinate of the element center
 * @param {number} centerY - The y coordinate of the element center
 * @returns {number} - The angle in degrees (0-359)
 */
export function calculateAngleFromMouse(mouseX, mouseY, centerX, centerY) {
  const dx = mouseX - centerX;
  const dy = mouseY - centerY;

  // atan2 returns angle in radians from -PI to PI
  // where 0 is pointing right (3 o'clock position)
  let angleRadians = Math.atan2(dy, dx);

  // Convert to degrees
  let angleDegrees = angleRadians * (180 / Math.PI);

  // Adjust so that 0 degrees is at the bottom (6 o'clock)
  // atan2 has 0 at 3 o'clock, so we add 90 to shift it
  angleDegrees = angleDegrees + 90;

  // Normalize to 0-359 range
  return normalizeAngle(angleDegrees);
}

/**
 * Normalizes an angle to the 0-359 range.
 *
 * @param {number} angle - The angle in degrees (can be any value)
 * @returns {number} - The normalized angle in range 0-359
 */
export function normalizeAngle(angle) {
  // Guard against NaN or non-finite values
  const numAngle = Number(angle);
  if (!Number.isFinite(numAngle)) {
    return 0;
  }

  let normalized = numAngle % 360;

  if (normalized < 0) {
    normalized += 360;
  }

  // Round and ensure we stay in 0-359 range (rounding 359.5+ would give 360)
  const rounded = Math.round(normalized) % 360;

  // Add 0 to convert -0 to 0
  return rounded + 0;
}

/**
 * Returns the opposite side of the given side.
 * Used to position the rotate handle on the opposite side from the context menu.
 *
 * @param {string} highestSide - The highest side ("top", "right", "bottom", "left")
 * @returns {string} - The opposite (lowest) side
 */
export function getLowestSide(highestSide) {
  const opposites = {
    top: "bottom",
    bottom: "top",
    left: "right",
    right: "left"
  };
  return opposites[highestSide] || "bottom";
}

/**
 * Calculates the cumulative rotation angle for a multi-element selection.
 * This is the shared logic used by both RotateButton and RotateHandler
 * to ensure consistency across the app.
 *
 * @param {Array} elements - Array of elements with angle property
 * @returns {number} - The cumulative rotation angle:
 *   - If all elements have the same angle: that angle
 *   - If elements have different angles: 0 (mixed state)
 */
export function getMultiElementRotationAngle(elements) {
  if (!elements || elements.length === 0) {
    return 0;
  }

  const angles = new Set(
    elements.map(el => normalizeAngle(Number(el?.angle) || 0))
  );

  if (angles.size === 1) {
    return Array.from(angles)[0];
  }

  return 0;
}

/**
 * Calculates the centroid (center point) of a collection of elements.
 * The centroid is the average of all element center points.
 *
 * @param {Array} elements - Array of elements with left, top, width, height properties
 * @returns {{ x: number, y: number }} - The centroid coordinates in design space
 */
export function calculateSelectionCentroid(elements) {
  if (!elements || elements.length === 0) {
    return { x: 0, y: 0 };
  }

  let xSum = 0;
  let ySum = 0;

  elements.forEach(element => {
    xSum += element.left + element.width / 2;
    ySum += element.top + element.height / 2;
  });

  return {
    x: xSum / elements.length,
    y: ySum / elements.length
  };
}
